import { createSlice } from "@reduxjs/toolkit";
import { fetchSurvey } from "~/thunks/surveyThunks";
import { fetchAsset, fetchAssets } from "~/thunks/assetsThunks";
import { fetchAppData } from "~/thunks/appDataThunks";
import type { StatutsType } from "~/types/general";
import { createAssetThunk, createProviderThunk, createSurveysThunk, createUserThunk } from "~/thunks/createThunks";
import { createOrganization } from "~/thunks/adminThunks";

interface AppState {
  message: string;
  error: string;
  assets: any[],
  appData: {
    statuses:  { [key in StatutsType]: any[] },
    roleTypes: any[],
    verticals: any[],
  }
  loading: boolean;
  menuItem: "" | "Assets" | "Surveys" | "Settings";
  notifications: {
    id: string;
    text: string;
    type: "error" | "info" | "success" | "working";
  }[];
}


const initialState: AppState = {
  message: "",
  error: "",
  assets: [],
  notifications: [],
  appData: {
    statuses: { visibility: [], task: [], general: [], survey: [], workOrder: [], element: [], connection: [] },
    roleTypes: [],
    verticals: [],
  },
  menuItem: "",
  loading: false,
};

const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setMessage(state, action) {
      state.message = action.payload;
    },
    clearMessage(state) {
      state.message = "";
    },
    setAssets(state, action) {
      state.assets = action.payload;
    },
    setMenuItem(state, action) {
      state.menuItem = action.payload;
    },
    setAppData(state, action) {
      console.log("setAppData", action.payload);
      state.appData = action.payload;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    addNotification(state, action) {
      // Remove any existing "working" toasts when a new toast is added
      state.notifications = state.notifications.filter(notification => notification.type !== "working");
      state.notifications.push(action.payload);
    },
    removeNotification(state, action) {
      state.notifications = state.notifications.filter((notification) => notification.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSurvey.pending, (state, action) => {
        state.message = "Loading survey data...";
      })
      .addCase(fetchSurvey.fulfilled, (state) => {
        state.message = "";
      })
      .addCase(fetchSurvey.rejected, (state, action) => {

        state.message = "";
        state.error = action.payload as string || "Failed to load survey data";
        
      })
      .addCase(fetchAsset.pending, (state, action) => {
        state.message = "Loading asset data...";
      })
      .addCase(fetchAsset.fulfilled, (state) => {
        
        state.message = "";
      })
      // .addCase(createOrganization.pending, (state, action) => {
      //   state.message = "Creating organization...";
      // })
      // .addCase(createOrganization.fulfilled, (state) => {
      //   state.message = "";
      // })
      .addCase(fetchAssets.pending, (state, action) => {
        state.message = "Getting assets...";
      })
      .addCase(fetchAssets.fulfilled, (state, action) => {
        state.assets = action.payload;
        state.message = "";
      })
      // .addCase(fetchAppData.pending, (state, action) => {
      //   state.message = "Loading application data...";
      // })
      .addCase(fetchAppData.fulfilled, (state, action) => {
        console.log("fetchAppData fulfilled", action.payload);
        const statuses = action.payload.statuses || [];
        const statusesByType: { [key in StatutsType]: any[] } = {
          visibility: statuses.filter((s: any) => s.type === "visibility"),
          task: statuses.filter((s: any) => s.type === "task"),
          general: statuses.filter((s: any) => s.type === "generalUser"),
          survey: statuses.filter((s: any) => s.type === "survey"),
          workOrder: statuses.filter((s: any) => s.type === "workOrder"),
          element: statuses.filter((s: any) => s.type === "element"),
          connection: statuses.filter((s: any) => s.type === "connection"),
        };
        state.appData = {
          ...state.appData,
          statuses: statusesByType,
          roleTypes: action.payload.roleTypes || [],
          verticals: action.payload.verticals || [],
        };

      })
      .addCase(createProviderThunk.pending, (state, action) => {
        state.message = "Creating provider...";
      })
      .addCase(createProviderThunk.fulfilled, (state) => {
        state.message = "";
      })
      .addCase(createAssetThunk.pending, (state, action) => {
        state.message = "Creating asset and folders...";
      })
      .addCase(createAssetThunk.fulfilled, (state) => {
        state.message = "";
      })
      // .addCase(createSurveysThunk.pending, (state, action) => {
      //   state.message = "Creating surveys and tasks...";
      // })
      // .addCase(createSurveysThunk.fulfilled, (state) => {
      //   state.message = "";
      // })
      .addCase(createUserThunk.pending, (state, action) => {
        state.message = "Creating user...";
      })
      .addCase(createUserThunk.fulfilled, (state) => {
        state.message = "";
      })

  },
});
  


export const {setLoading, addNotification, removeNotification, setMessage, setAppData, clearMessage, setMenuItem } = appSlice.actions;
export const selectMessage = (state: { app: AppState }) => state.app.message;
export const selectAssets = (state: { app: AppState }) => state.app.assets;
export const selectMenuItem = (state: { app: AppState }) => state.app.menuItem;
export const selectAppData = (state: { app: AppState }) => state.app.appData;
export const selectLoading = (state: { app: AppState }) => state.app.loading;
export const selectNotifications = (state: { app: AppState }) => state.app.notifications;
export default appSlice.reducer;